import { faCircleNotch } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FightSetup, FullFight } from './data/models'
import { importSetup } from './data/setup-import-export'
import { ConfirmModal } from './modals/ConfirmModal'
import { DisplayFightActions } from './redux/slices/display-fight-slice'
import { useFightState, FightActions, useFightSelectors } from './redux/slices/fight-slice'
import { useSetupState, SetupActions } from './redux/slices/setup-slice'
import { areFightsEqual, areSetupsEqual } from './utils/compare-utils'
import { joinLines } from './utils/utils'

export const GistImport = (): JSX.Element | null => {

    const [isLoading, setIsLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)

    const disp = useDispatch()
    const fightState = useFightState()
    const fightStateSel = useFightSelectors()
    const setupState = useSetupState()

    const importRef = useRef<{
        fullFight?: FullFight
        setup?: FightSetup
    }>({})

    useEffect(() => {
        /*
            There are no extra integrity checks here. This entire
            feature acts like a manual import, only the import is
            fetched from a gist.
        */

        const fn = async () => {
            const [gistUser, gistId] = window.location.hash.replace("#", "").split(";")

            if (gistId) {
                setIsLoading(true)

                await axios.get<string>(`https://gist.githubusercontent.com/${gistUser}/${gistId}/raw/`)
                    .then(result => {
                        const fightString = result.data

                        if (!fightString.startsWith("export[")) {
                            alert('Import failed, invalid import payload')
                            return
                        }

                        const importData = importSetup({ exportStr: fightString, skipClone: true })

                        const fightExists = fightState.fights
                            .find(f => f.id === importData.fullFight.fight.id)

                        if (fightExists) {
                            const currFullFight = fightStateSel.getFullFight(fightExists.id)

                            if (!areFightsEqual(currFullFight, importData.fullFight)) {
                                importRef.current.fullFight = importData.fullFight
                            }
                        } else {
                            disp(FightActions.addNewFight({
                                ...importData.fullFight
                            }))
                            disp(DisplayFightActions.selectFight({ fightId: importData.fullFight.fight.id }))
                        }

                        if (importData.setup) {
                            const setupId = importData.setup.id
                            const setupExists = setupState.setups.find(s => s.id === setupId)

                            if (setupExists) {
                                if (!areSetupsEqual(setupExists, importData.setup)) {
                                    importRef.current.setup = importData.setup
                                }
                            } else {
                                disp(SetupActions.addNewSetup({
                                    setup: importData.setup
                                }))
                            }
                        }

                        if (importRef.current.fullFight || importRef.current.setup) {
                            setShowModal(true)
                        }
                    })
                    .catch(error => {
                        alert('Import failed, see console')
                        console.log(error)
                    })
                    .finally(() => {
                        setIsLoading(false)
                    })
            }
        }

        fn()

        // Don't put shit in here, this is a fire and forget
        //
        // eslint-disable-next-line
    }, [])

    return (
        <div>
            {isLoading && (
                <div
                    style={{
                        zIndex: 3,
                        position: 'fixed',
                        top: '0',
                        left: '0',
                        bottom: '0',
                        right: '0',
                        overflow: 'auto',
                        opacity: 0.8,
                        background: 'black',
                        color: 'white'
                    }}
                    className="d-flex align-items-center justify-content-center"
                >
                    <div className="d-flex align-items-end">
                        <FontAwesomeIcon icon={faCircleNotch} spin fixedWidth className="mr-2 h2 p-0 m-0" />
                        <div className="h2 p-0 m-0">
                            {'loading...'}
                        </div>
                    </div>
                </div>
            )}
            {showModal && (

                <ConfirmModal
                    title={'Overwrite fight & setup?'}
                    btnColor="danger"
                    closeSelf={function (): void {
                        setShowModal(false)
                    }}
                    content={joinLines([
                        `You've already imported either this outline or setup. If you've made changes to either, this import will overwrite those changes.`,
                        "<br/>",
                        "<br/>",
                        "Any other setups on this outline will not be affected. However, if the outline has changed significantly, some assignments maybe gone.",
                    ])}

                    onConfirm={function (): void {
                        if (importRef.current.fullFight) {
                            disp(FightActions.updateFight({
                                ...importRef.current.fullFight
                            }))

                            disp(DisplayFightActions.selectFight({ fightId: importRef.current.fullFight.fight.id }))
                        }

                        if (importRef.current.setup) {
                            disp(SetupActions.updateSetup({
                                setup: importRef.current.setup
                            }))

                            disp(DisplayFightActions.selectFight({ fightId: importRef.current.setup.fightId }))
                        }
                    }}
                />
            )}

        </div>
    )
}
